import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { FiCrosshair, FiMapPin } from 'react-icons/fi';
import { isGoogleMapsAuthFailed, loadGoogleMaps, parsePlaceComponents } from '../../utils/loadGoogleMaps';
import { currentUiLanguage } from '../../utils/uiLanguage';
import styles from './styles.module.css';

// Safety net on top of the native `getCurrentPosition` `timeout` option: some
// browsers (mobile in-app webviews in particular — WhatsApp/Instagram/
// Facebook — are the common offender) never invoke *either* callback when
// the permission prompt they'd need to show isn't supported, silently
// ignoring the requested timeout too. Without this, "current location" spins
// forever with no way out except reloading the page.
const GEO_TIMEOUT_MS = 10000;

// Debounce keystrokes before hitting the Places API — avoids firing a
// request per character while still feeling instant.
const SUGGEST_DEBOUNCE_MS = 220;
const MIN_QUERY_LENGTH = 3;
// The suggestions list's own max height (styles: .suggestionsPanel), and the
// room below the field under which it opens upward instead (UX-SHOP-16).
const PANEL_MAX_HEIGHT = 260;
const PANEL_MIN_ROOM = 200;
const PHONE_MAX_WIDTH = 768;

// Every Places and Geocoder request names the language the page is read in.
// The Maps API is loaded without one, so Google otherwise answers in the
// BROWSER's: "הרצל 10" typed into a Hebrew checkout on an English phone came
// back as "Herzl St 10, Haifa, Israel".
const inPageLanguage = (request) => {
  const language = currentUiLanguage();
  return language ? { ...request, language } : request;
};

/**
 * Google Places address picker with "use current location".
 * Emits a structured location: { address, city, country, lat, lng }.
 *
 * Renders its own suggestions dropdown (styled to match the app, e.g. the
 * portal's onboarding combobox) instead of Google's default `.pac-container`
 * widget — that widget is appended to <body> outside our control, so it
 * fought us on both styling and z-index/clipping inside scrollable bottom
 * sheets. Predictions come from `AutocompleteService`; picking one resolves
 * full details via `Geocoder.geocode({ placeId })`.
 */
const DeliveryAddressPicker = ({
  value,
  onChange,
  label,
  placeholder,
  error,
  testIdPrefix = 'delivery-address',
  t,
  // Called when the address search cannot work at all — the Maps script did
  // not load, or Google refused the request — so the parent can offer a way to
  // type the address instead of leaving a search that can never answer.
  onUnavailable,
  // What the browser may autofill here ("shipping street-address" on a checkout); off by default, as it was,
  // so the browser's own suggestions do not fight the list below.
  autoComplete = 'off',
}) => {
  const inputRef = useRef(null);
  const rowRef = useRef(null);
  const valueRef = useRef(value);
  const autocompleteServiceRef = useRef(null);
  const debounceRef = useRef(null);
  const requestIdRef = useRef(0);
  const [locating, setLocating] = useState(false);
  const [locError, setLocError] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [panelRect, setPanelRect] = useState(null);

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  useEffect(() => {
    if (inputRef.current && value?.address !== undefined && inputRef.current.value !== value.address) {
      inputRef.current.value = value.address;
    }
  }, [value?.address]);

  useEffect(() => () => window.clearTimeout(debounceRef.current), []);

  // Keep the (portaled) dropdown glued under the input row — it lives in
  // document.body, not the DOM subtree, so it needs its own position instead
  // of relying on normal layout flow.
  useEffect(() => {
    if (!open) return undefined;
    // On a phone the field often sits near the bottom of the screen, where a
    // list opened below it lands under the keyboard. When there is not room
    // below and there is more above, it opens upward (UX-SHOP-16). The room is
    // measured against the VISUAL viewport, which shrinks when the keyboard
    // opens; the fixed position is measured against the layout viewport.
    const updateRect = () => {
      if (!rowRef.current) return;
      const rect = rowRef.current.getBoundingClientRect();
      const layoutHeight = window.innerHeight || 0;
      const visibleHeight = window.visualViewport?.height || layoutHeight;
      const roomBelow = visibleHeight - rect.bottom;
      const roomAbove = rect.top;
      if (roomBelow < PANEL_MIN_ROOM && roomAbove > roomBelow) {
        setPanelRect({
          bottom: layoutHeight - rect.top + 4,
          left: rect.left,
          width: rect.width,
          maxHeight: Math.max(120, Math.min(PANEL_MAX_HEIGHT, roomAbove - 8)),
        });
      } else {
        setPanelRect({
          top: rect.bottom + 4,
          left: rect.left,
          width: rect.width,
          maxHeight: Math.max(120, Math.min(PANEL_MAX_HEIGHT, roomBelow - 8)),
        });
      }
    };
    updateRect();
    window.addEventListener('scroll', updateRect, true);
    window.addEventListener('resize', updateRect);
    window.visualViewport?.addEventListener?.('resize', updateRect);
    return () => {
      window.removeEventListener('scroll', updateRect, true);
      window.removeEventListener('resize', updateRect);
      window.visualViewport?.removeEventListener?.('resize', updateRect);
    };
  }, [open]);

  const fetchSuggestions = (query) => {
    const trimmed = query.trim();
    if (trimmed.length < MIN_QUERY_LENGTH) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    const requestId = ++requestIdRef.current;
    loadGoogleMaps().then(() => {
      // Stale response for an older keystroke, or the field lost focus already.
      if (requestId !== requestIdRef.current) return;
      if (!autocompleteServiceRef.current) {
        autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
      }
      autocompleteServiceRef.current.getPlacePredictions(inPageLanguage({ input: trimmed }), (predictions, status) => {
        if (requestId !== requestIdRef.current) return;
        // REQUEST_DENIED is Google refusing the key or the service itself (a
        // referrer rule, billing, an API closed to new customers) — not "no
        // match". Nothing typed next will get an answer either.
        if (status === 'REQUEST_DENIED' || isGoogleMapsAuthFailed()) onUnavailable?.();
        setSuggestions(status === 'OK' && predictions ? predictions : []);
        setActiveIndex(-1);
        setOpen(true);
      });
    }).catch(() => {
      if (requestId !== requestIdRef.current) return;
      setSuggestions([]);
      setOpen(false);
      onUnavailable?.();
    });
  };

  const handleInputChange = (e) => {
    const address = e.target.value;
    setLocError('');
    onChange({
      address,
      city: value?.city || '',
      country: value?.country || '',
      lat: null,
      lng: null,
    });
    window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => fetchSuggestions(address), SUGGEST_DEBOUNCE_MS);
  };

  const selectSuggestion = (prediction) => {
    requestIdRef.current += 1; // invalidate any in-flight prediction fetch
    setOpen(false);
    setSuggestions([]);
    setActiveIndex(-1);
    loadGoogleMaps().then(() => {
      new window.google.maps.Geocoder().geocode(inPageLanguage({ placeId: prediction.place_id }), (results, status) => {
        if (status !== 'OK' || !results?.[0]) return;
        const details = parsePlaceComponents(results[0]);
        if (inputRef.current) inputRef.current.value = details.address;
        setLocError('');
        onChange(details);
      });
    }).catch(() => {});
  };

  // Best-effort fallback for when the customer types a full address but never
  // picks a suggestion — whether because they typed faster than predictions
  // arrived, dismissed the dropdown, or predictions never came back at all
  // (key/referrer issue on their domain, etc). Runs a direct Geocoder lookup
  // on whatever they typed so the address can still get lat/lng for
  // shipping, instead of leaving them silently stuck with an address that
  // "looks filled in" but was never accepted.
  const geocodeTypedAddress = () => {
    const current = valueRef.current;
    const address = (inputRef.current?.value || '').trim();
    // Already resolved (e.g. a suggestion was just picked) or nothing to look up.
    if (!address || current?.lat != null) return;
    loadGoogleMaps().then(() => {
      new window.google.maps.Geocoder().geocode(inPageLanguage({ address }), (results, status) => {
        if (status !== 'OK' || !results?.[0]) return;
        // A LOOSE match is not this address: Google found somewhere near some
        // of the words. Accepting it gave free text ("… - actually Herzl 99
        // Tel Aviv") the coordinates of a different street (UX-FINAL-17). The
        // address stays unverified and the shopper picks a suggestion.
        if (results[0].partial_match) return;
        const details = parsePlaceComponents(results[0]);
        // Keep exactly what the customer typed as the visible text — only
        // borrow lat/lng/city/country from the match. Silently rewriting
        // their input to Google's canonical formatting on a loose match
        // would look like the app is "changing" what they asked for.
        onChange({ ...details, address });
      });
    }).catch(() => onUnavailable?.());
  };

  // On a phone, bring the field up the screen when it is focused, so the
  // keyboard does not cover it and its suggestions have room (UX-SHOP-16).
  // Deferred so the keyboard has started to open.
  const handleFocus = () => {
    if (typeof window === 'undefined' || (window.innerWidth || 0) > PHONE_MAX_WIDTH) return;
    window.setTimeout(() => {
      const row = rowRef.current;
      if (row && typeof row.scrollIntoView === 'function') {
        row.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    }, 300);
  };

  const handleBlur = () => {
    // Suggestion buttons preventDefault their mousedown, so a real click
    // never reaches here — this only fires for a genuine focus-away. Still
    // deferred slightly so a tap-driven selection has room to land first.
    window.setTimeout(() => {
      setOpen(false);
      geocodeTypedAddress();
    }, 120);
  };

  const handleKeyDown = (e) => {
    if (open && suggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((i) => (i + 1) % suggestions.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
        return;
      }
      if (e.key === 'Escape') {
        setOpen(false);
        return;
      }
      if (e.key === 'Enter' && activeIndex >= 0) {
        e.preventDefault();
        selectSuggestion(suggestions[activeIndex]);
        return;
      }
    }
    if (e.key === 'Enter') {
      // Nothing highlighted in the dropdown — fall back to geocoding
      // whatever's currently typed.
      window.setTimeout(geocodeTypedAddress, 30);
    }
  };

  const handleCurrentLocation = () => {
    setOpen(false);
    if (!navigator.geolocation) {
      setLocError(t('deliveryLocation.geoUnsupported', { defaultValue: 'Location is not supported on this device.' }));
      return;
    }
    // Browsers silently deny geolocation on insecure (non-HTTPS, non-localhost)
    // origins — the callback below would just report a generic "denied"
    // error, which reads like a permissions problem the customer can "fix"
    // by allowing access again. Surface the real cause up front instead.
    if (typeof window !== 'undefined' && window.isSecureContext === false) {
      setLocError(t('deliveryLocation.geoInsecureContext', {
        defaultValue: 'Location requires a secure connection — search for your address instead.',
      }));
      return;
    }
    setLocating(true);
    setLocError('');

    // Some browsers/in-app webviews (WhatsApp, Instagram, Facebook) never
    // invoke *either* geolocation callback when they can't show a permission
    // prompt, silently ignoring the `timeout` option below too — that's what
    // makes the button spin forever with no way out but reloading. This
    // watchdog guarantees we always land on an error within a bounded time.
    let settled = false;
    const finish = (fn) => {
      if (settled) return;
      settled = true;
      setLocating(false);
      fn?.();
    };
    const watchdog = window.setTimeout(() => {
      finish(() => setLocError(t('deliveryLocation.geoTimeout', {
        defaultValue: 'Location is taking too long — search for your address instead.',
      })));
    }, GEO_TIMEOUT_MS);

    navigator.geolocation.getCurrentPosition(
      ({ coords: { latitude: lat, longitude: lng } }) => {
        loadGoogleMaps().then(() => {
          new window.google.maps.Geocoder().geocode(inPageLanguage({ location: { lat, lng } }), (results, status) => {
            window.clearTimeout(watchdog);
            finish(() => {
              if (status !== 'OK' || !results?.[0]) {
                setLocError(t('deliveryLocation.geoFailed', { defaultValue: 'Could not resolve your location.' }));
                return;
              }
              const details = { ...parsePlaceComponents(results[0]), lat, lng };
              if (inputRef.current) inputRef.current.value = details.address;
              onChange(details);
            });
          });
        }).catch(() => {
          window.clearTimeout(watchdog);
          onUnavailable?.();
          finish(() => setLocError(
            isGoogleMapsAuthFailed()
              ? t('deliveryLocation.mapsAuthFailed', { defaultValue: 'Address search is unavailable right now — please type your address instead.' })
              : t('deliveryLocation.mapsLoadFailed', { defaultValue: 'Could not load maps.' }),
          ));
        });
      },
      () => {
        window.clearTimeout(watchdog);
        finish(() => setLocError(t('deliveryLocation.geoDenied', { defaultValue: 'Allow location access or search for your address.' })));
      },
      { timeout: 8000, enableHighAccuracy: true },
    );
  };

  const hint = value?.lat != null
    ? t('deliveryLocation.coordsReady', { defaultValue: 'Location verified — shipping prices are based on distance.' })
    : t('deliveryLocation.searchHint', { defaultValue: 'Search for your address or tap the pin for current location.' });

  const listboxId = `${testIdPrefix}-listbox`;
  const showPanel = !!(open && suggestions.length > 0 && panelRect);

  return (
    <div className={styles.wrap}>
      {label && (
        <span className={styles.label} id={`${testIdPrefix}-label`}>
          {label}
        </span>
      )}
      <div
        ref={rowRef}
        className={`${styles.row} ${error || locError ? styles.rowError : ''}`}
      >
        <FiMapPin className={styles.leadingIcon} aria-hidden />
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          className={styles.input}
          defaultValue={value?.address || ''}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder={placeholder || t('deliveryLocation.placeholder', { defaultValue: 'Search address or city…' })}
          autoComplete={autoComplete}
          autoCorrect="off"
          spellCheck={false}
          aria-labelledby={label ? `${testIdPrefix}-label` : undefined}
          aria-expanded={showPanel ? 'true' : 'false'}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={activeIndex >= 0 ? `${testIdPrefix}-option-${activeIndex}` : undefined}
          data-testid={`${testIdPrefix}-input`}
        />
        <button
          type="button"
          className={styles.locateBtn}
          onClick={handleCurrentLocation}
          disabled={locating}
          title={t('deliveryLocation.useCurrentLocation', { defaultValue: 'Use current location' })}
          aria-label={t('deliveryLocation.useCurrentLocation', { defaultValue: 'Use current location' })}
          data-testid={`${testIdPrefix}-locate`}
        >
          {/* NOT A SECOND PIN. The field already wears one for "a place"; this button means "find ME", and two
              identical pins at either end of one row read as an icon that lost its meaning. */}
          {locating ? <span className={styles.locatingDot} aria-hidden /> : <FiCrosshair aria-hidden />}
        </button>
      </div>
      {(error || locError) && (
        <div className={styles.error} role="alert">{error || locError}</div>
      )}
      {!error && !locError && (
        <p className={styles.hint}>{hint}</p>
      )}
      {showPanel && ReactDOM.createPortal(
        <div
          className={styles.suggestionsPanel}
          style={{
            top: panelRect.top,
            bottom: panelRect.bottom,
            left: panelRect.left,
            width: panelRect.width,
            maxHeight: panelRect.maxHeight,
          }}
          role="listbox"
          id={listboxId}
          data-testid={`${testIdPrefix}-suggestions`}
        >
          {suggestions.map((s, i) => (
            <button
              key={s.place_id}
              type="button"
              role="option"
              id={`${testIdPrefix}-option-${i}`}
              aria-selected={i === activeIndex}
              className={`${styles.suggestionItem} ${i === activeIndex ? styles.suggestionItemActive : ''}`}
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setActiveIndex(i)}
              onClick={() => selectSuggestion(s)}
              data-testid={`${testIdPrefix}-suggestion-${i}`}
            >
              <span className={styles.suggestionMain}>
                {s.structured_formatting?.main_text || s.description}
              </span>
              {s.structured_formatting?.secondary_text && (
                <span className={styles.suggestionSub}>{s.structured_formatting.secondary_text}</span>
              )}
            </button>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
};

export default DeliveryAddressPicker;
